// ============================================================
// Chess Arena — Collision Resolver
// ============================================================

import { GridPos, PieceType } from './types';
import { Piece } from './piece';

export interface CaptureEvent {
  /** The piece that wins (survives). */
  winnerId: string;
  /** The piece that is captured (destroyed). */
  loserId: string;
}

/** Pawns can only capture diagonally — reject orthogonal captures. */
function canPawnCapture(pawn: Piece): boolean {
  const target = pawn.pieceState.moveTarget;
  if (!target) return false;
  const origin = pawn.pieceState.pos;
  // Diagonal means both col and row differ
  return origin.col !== target.col && origin.row !== target.row;
}

/** Knights only capture at their destination, not along the L-shaped path. */
function isKnightAtDestination(
  knight: Piece,
  getCellSize: () => number,
  getBoardOffset: () => { x: number; y: number },
): boolean {
  const target = knight.pieceState.moveTarget;
  if (!target) return false;
  const cellSize = getCellSize();
  const offset = getBoardOffset();
  const targetX = offset.x + target.col * cellSize + cellSize / 2;
  const targetY = offset.y + target.row * cellSize + cellSize / 2;
  const dx = knight.x - targetX;
  const dy = knight.y - targetY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist < cellSize * 0.4;
}

/** Check whether the attacker is allowed to capture the target at their current position. */
function canCapture(
  attacker: Piece,
  getCellSize: () => number,
  getBoardOffset: () => { x: number; y: number },
): boolean {
  if (attacker.pieceState.type === 'Pawn') return canPawnCapture(attacker);
  if (attacker.pieceState.type === 'Knight') return isKnightAtDestination(attacker, getCellSize, getBoardOffset);
  return true;
}

/**
 * Check for mid-transit collisions among moving pieces.
 *
 * Rules (from GDD 2.3):
 * - If two opposing pieces occupy the same cell or cross paths while both
 *   are moving, the piece that started moving first (earlier moveStartTime) wins.
 * - If a moving piece hits a stationary piece, the moving piece captures it.
 *
 * @param movingPieces All pieces that are currently in-transit (isMoving === true).
 * @param allPieces    All alive pieces (moving + stationary) for stationary collision checks.
 * @param getCellSize  Function returning cell size in pixels.
 * @param getBoardOffset Function returning board offset { x, y }.
 * @returns Array of CaptureEvents to process.
 */
export function checkCollisions(
  movingPieces: Piece[],
  allPieces: Piece[],
  getCellSize: () => number,
  getBoardOffset: () => { x: number; y: number },
): CaptureEvent[] {
  const captures: CaptureEvent[] = [];
  const alreadyResolved = new Set<string>();

  const cellSize = getCellSize();
  const collisionThreshold = cellSize * 0.4; // close enough = collision

  // --- Moving vs Moving collisions ---
  for (let i = 0; i < movingPieces.length; i++) {
    const a = movingPieces[i];
    if (!a.pieceState.isAlive || alreadyResolved.has(a.pieceState.id)) continue;

    for (let j = i + 1; j < movingPieces.length; j++) {
      const b = movingPieces[j];
      if (!b.pieceState.isAlive || alreadyResolved.has(b.pieceState.id)) continue;

      // Only check opposing teams
      if (a.pieceState.team === b.pieceState.team) continue;

      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < collisionThreshold) {
        // First mover wins
        if (a.pieceState.moveStartTime <= b.pieceState.moveStartTime) {
          if (!canCapture(a, getCellSize, getBoardOffset)) continue;
          captures.push({ winnerId: a.pieceState.id, loserId: b.pieceState.id });
          alreadyResolved.add(b.pieceState.id);
        } else {
          if (!canCapture(b, getCellSize, getBoardOffset)) continue;
          captures.push({ winnerId: b.pieceState.id, loserId: a.pieceState.id });
          alreadyResolved.add(a.pieceState.id);
        }
      }
    }
  }

  // --- Moving vs Stationary collisions ---
  for (const mover of movingPieces) {
    if (!mover.pieceState.isAlive || alreadyResolved.has(mover.pieceState.id)) continue;

    for (const target of allPieces) {
      if (!target.pieceState.isAlive) continue;
      if (target.pieceState.isMoving) continue; // handled above
      if (target.pieceState.team === mover.pieceState.team) continue;
      if (alreadyResolved.has(target.pieceState.id)) continue;

      const dx2 = mover.x - target.x;
      const dy2 = mover.y - target.y;
      const dist = Math.sqrt(dx2 * dx2 + dy2 * dy2);
      if (dist < collisionThreshold) {
        if (!canCapture(mover, getCellSize, getBoardOffset)) continue;
        // Moving piece captures stationary piece
        captures.push({ winnerId: mover.pieceState.id, loserId: target.pieceState.id });
        alreadyResolved.add(target.pieceState.id);
      }
    }
  }

  return captures;
}
